import { Component, effect, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { SharedModule } from 'primeng/api';
import { TagModule } from "primeng/tag";
import { ConsequencesDialogService, ConsequencesMetrics } from './consequences-dialog.service';
import { TaskMovementService } from 'src/app/shared/services/operations/task-movement.service';
import { DataRepositoryService } from 'src/app/shared/services/data-repository/data-repository.service';
import { TaskInfoService } from 'src/app/shared/services/task-info/task-info.service';
import { TBTabName } from 'src/app/shared/entity/enums';
import { GramsToKgPipe } from 'src/app/shared/pipe/utility-pipes/data-formatters/grams-to-kg.pipe';

@Component({
    selector: 'app-consequences-dialog',
    standalone: true,
    imports: [SharedModule, TagModule, GramsToKgPipe],
    templateUrl: './consequences-dialog.component.html',
    styleUrls: ['./consequences-dialog.component.scss']
})
export class ConsequencesDialogComponent implements OnInit {
  @ViewChild('dialogFooter', { static: true }) dialogFooter!: TemplateRef<any>;

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  consequencesMetrics: ConsequencesMetrics = {} as ConsequencesMetrics;

  /**
   * should be used to check if the KPI data is available for display. It checks if the provided KPI object has any of the expected properties (taskCount, itemCount, totalWeight, stopCount) defined.
   * @param kpi data object to check for KPI properties. The method returns true if at least one of the KPI properties is defined, indicating that there is valid KPI data available for display. If none of the properties are defined, it returns false, indicating that there is no valid KPI data to show.
   * @returns   A boolean value indicating whether the provided KPI object has any of the expected properties defined (true) or not (false).
   */
  private hasKpiData(kpi: any): boolean {
    return !!kpi && (
      kpi.taskCount !== undefined ||
      kpi.itemCount !== undefined ||
      kpi.totalWeight !== undefined ||
      kpi.stopCount !== undefined
    );
  }

  /**
   * determines whether there is impact data available for the target route in the consequences metrics. It checks 
   * if the target route impact data exists and if it contains either a route ID or a route label, which are indicators of valid impact data for the target route.
   * @returns  A boolean value indicating whether there is impact data available for the target route in the consequences metrics (true) or not (false). It returns true if the target route 
   * impact data exists and contains either a route ID or a route label, and false otherwise.
   */
  hasTargetRouteImpact(): boolean {
    const target = this.consequencesMetrics?.targetRouteImpact as any;
    return !!target && (!!target.routeId || !!target.routeLabel);
  }

  /**
   * This method is used to determine if there are any consequences data available for display in the consequences dialog. 
   * It checks if there is valid KPI data for the task movement, if there are any source routes impact data, or
   * if there is valid impact data for the target route. If any of these conditions are true, it indicates that there is consequences data available for display, and the method returns true. 
   * If none of these conditions are met, it returns false, indicating that there is no consequences data to show.
   * @returns  A boolean value indicating whether there are any consequences data available for display (true) or not (false).
   */
  hasMoveTaskMetrics(): boolean {
    return this.hasKpiData(this.consequencesMetrics?.moveTaskMetrics as any);
  }

  /**
   * This method checks if there is any impact data available for the source routes in the consequences metrics.
   *  It verifies if the source routes impact data exists and if it contains any entries,
   *  which would indicate that there are consequences data related to the source routes available for display.
   * @returns A boolean value indicating whether there is any impact data available for the source routes in the consequences metrics (true) or not (false).
   *  It returns true if the source routes impact data exists and contains at least one entry, and false otherwise.
   */ 
  hasSourceRoutesImpact(): boolean {
    return (this.consequencesMetrics?.sourceRoutesImpact?.length ?? 0) > 0;
  }

  /**
   * This method checks if there is any consequences data available for display in the consequences dialog.
   *  It does this by checking if there are valid KPI data for the task movement, 
   * if there are any source routes impact data, or if there is valid impact data for the target route.
   *  If any of these conditions are true, it indicates that there is consequences data available for display, 
   * @returns A boolean value indicating whether there are any consequences data available for display (true) or not (false).
   */
  hasConsequencesData(): boolean {
    return this.hasTargetRouteImpact() || this.hasMoveTaskMetrics() || this.hasSourceRoutesImpact();
  }

  constructor(private readonly consequencesDialogService: ConsequencesDialogService,
    private readonly dataRepositoryService: DataRepositoryService,
    private readonly taskMovementService: TaskMovementService,
    private readonly taskInfoService: TaskInfoService
  ) {
    this.listenerForConsequenceMetrics();
  }

  ngOnInit(): void {
    this.getConsequencesMetrics();
  }

  /**
   * Fetches the consequences metrics for the task movement operation by constructing a payload with the necessary information such as source route IDs, target route ID, and optionally task IDs or item IDs based on the selected tab.
   * It then calls the getConsequencesForMovement method of the ConsequencesDialogService to retrieve the consequences metrics based on the provided payload. 
   * The retrieved consequences metrics will be used to display the potential impacts of moving tasks from one route to another in the consequences dialog.
   */
  getConsequencesMetrics() {
    const taskIdMap = this.dataRepositoryService.movingTasksMap;
    const taskIds = taskIdMap ? Array.from(taskIdMap.keys()) : [];
    const isUnloadedTab = this.taskInfoService.selectedTabName.toLowerCase() === TBTabName.Unloaded.toLowerCase();
    const payload: {
      sourceRouteIds: string[];
      targetRouteId: string;
      taskIds?: string[];
      itemIds?: string[];
    } = {
      sourceRouteIds: isUnloadedTab ? [] : this.taskMovementService.getUniqueRouteIdsFromMovingTasksMap() ?? [],
      targetRouteId: this.dataRepositoryService.taskMovementData.route_id ?? ''
    };

    if (taskIds.length > 0) {
      if (isUnloadedTab) {
        payload.itemIds = taskIds;
      } else {
        payload.taskIds = taskIds;
      }
    }
    this.consequencesDialogService.getConsequencesForMovement(payload);
  }

  /**
 * Sets up a reactive effect to listen for changes in the consequences route metrics provided by the ConsequencesDialogService.
 * Whenever the consequences route metrics are updated, this effect will update the local consequencesMetrics property with the new values.
 * This allows the component to reactively display the latest consequences metrics whenever they change. 
 */
  listenerForConsequenceMetrics(){
    effect(() => {
      if(this.consequencesDialogService.consequencesRouteMetrics()) {
        this.consequencesMetrics = this.consequencesDialogService.consequencesRouteMetrics();
      }
    });
  }

  /**
   * Closes the consequences dialog by emitting a false value to the visibleChange event emitter.
    * This method is typically called when the user wants to close the dialog, such as by clicking a close button.
   */
  close() {
    this.visibleChange.emit(false);
  }

  /**
   * Initiates the task movement process by calling the onSubmitTaskMovement method of the TaskMovementService.
   * After initiating the task movement, it emits a false value to the visibleChange event emitter to close the consequences dialog.
   * This method is typically called when the user confirms that they want to move the task(s) after reviewing the consequences. 
   */
  moveTask() {
    if (!this.hasConsequencesData()) {
      return;
    }
    this.taskMovementService.onSubmitTaskMovement()
    this.visibleChange.emit(false);
  }
}